/*
 * Decodes a JWT for display only — no signature verification, the app
 * just shows what the server issued. Real tokens have three base64url
 * segments (header.payload.signature); the simulated dev token is a
 * single base64 segment holding the claims directly.
 */
type Claims = Record<string, unknown> & {
  sub?: string;
  exp?: number;
  iat?: number;
  iss?: string;
  aud?: string | string[];
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
};

export function decodeToken(
  token: string | null | undefined
): { header: unknown; claims: Claims } | null {
  if (!token) return null;

  const decodeSegment = (segment: string): unknown => {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=');
    const bytes = Uint8Array.from(window.atob(padded), (char) =>
      char.charCodeAt(0)
    );
    return JSON.parse(new TextDecoder().decode(bytes));
  };

  try {
    const segments = token.split('.');
    if (segments.length === 3) {
      return {
        header: decodeSegment(segments[0]),
        claims: decodeSegment(segments[1]) as Claims,
      };
    }
    return { header: null, claims: decodeSegment(segments[0]) as Claims };
  } catch {
    return null;
  }
}

/* what to call the logged-in subject: the full name the identity
   provider issued, else the given/family names it always sends
   separately (the simulated dev token has no `name` claim), and only
   then the login id */
export function displayNameFromToken(
  token: string | null | undefined
): string | null {
  const claims = decodeToken(token)?.claims;
  if (!claims) return null;

  const composed = [claims.given_name, claims.family_name]
    .filter(Boolean)
    .join(' ');

  return claims.name || composed || claims.preferred_username || null;
}
